import { Geometry, Feature, Point } from 'geojson';
import { nearestPoint, featureCollection, point, nearestPointOnLine, feature } from '@turf/turf';
import { MapboxLayerType, ILayer, LayerSearchOptions, LayerFilter, LayerSearchResult } from '../layer.type';
import { MapElement } from '../../map-element.model';
import {_} from '~shared/utils';
import { IRegoFeatureDefinition } from '~app/linking/mapping';

export const getClickedPointForFeature = (clickedGeometry: Geometry, clickedMapPoint: Feature<Point>): Point => {
  switch (clickedGeometry.type) {
    case 'Point':
      return clickedGeometry;
    case 'MultiPoint': {
      const points = clickedGeometry.coordinates.map(c => point(c));
      const pointsAsFeatureCollection = featureCollection(points);
      const nearestP = nearestPoint(clickedMapPoint.geometry, pointsAsFeatureCollection);
      return nearestP.geometry;
    }
    case 'LineString':
    case 'MultiLineString': {
      const nearestP = nearestPointOnLine(clickedGeometry, clickedMapPoint.geometry.coordinates);
      return nearestP.geometry;
    }
    case 'GeometryCollection': { // Recursive call
      const nearestPoints = clickedGeometry.geometries.map(f => getClickedPointForFeature(f, clickedMapPoint));
      const pointsAsFeatures = featureCollection(nearestPoints.map(p => feature(p)));
      const nearestP = nearestPoint(clickedMapPoint, pointsAsFeatures);
      return nearestP.geometry;
    }
    case 'Polygon':       // Use the clicked point as the
    case 'MultiPolygon':  // point for polygons for now
    default:
      return clickedMapPoint.geometry;
  }
}

export const createMapboxLayer = (layerDefinition: ILayer): mapboxgl.Layer => {
  const mapboxLayer: mapboxgl.Layer = {
    id: layerDefinition.id,
    source: layerDefinition.id,
    type: layerDefinition.style.layerType as MapboxLayerType,
    layout: layerDefinition.mapboxOptions?.layout || {},
    paint: layerDefinition.mapboxOptions?.paint || {}
  };

  // Initial visibility
  mapboxLayer.layout.visibility = layerDefinition.style.visible ? 'visible' : 'none';

  return mapboxLayer;
}

export const createSymbolLayer = (layerDefinition: ILayer, imageId?: string): mapboxgl.Layer => {
  const mapboxLayer = createMapboxLayer(layerDefinition);

  const iconImage = layerDefinition.style.icon.urlExpression ?? imageId;
  if (iconImage) {
    mapboxLayer.layout = {
      'icon-allow-overlap': true,
      'icon-image': iconImage,
      'icon-size': .75,
      ...mapboxLayer.layout,
    };
  }
  if (layerDefinition.style?.icon?.rotation) {
    mapboxLayer.layout['icon-rotate'] = layerDefinition.style.icon.rotation;
  }
  if (layerDefinition.style?.icon?.opacity) {
    mapboxLayer.paint = {
      ...mapboxLayer.paint,
      'icon-opacity': layerDefinition.style.icon.opacity,
    };
  }
  if (layerDefinition.style?.icon?.textField) {
    mapboxLayer.paint = {
      'text-halo-color': 'white',
      'text-halo-width': 2,
      ...mapboxLayer.paint,
    };
    mapboxLayer.layout = {
      'text-field': layerDefinition.style.icon.textField,
      'text-radial-offset': 1,
      'text-size': 12,
      'text-allow-overlap': true,
      ...mapboxLayer.layout,
    };
  }
  return mapboxLayer;
}

export const createLineLayer = (layerDefinition: ILayer): mapboxgl.Layer => {
  const mapboxLayer = createMapboxLayer(layerDefinition);
  mapboxLayer.paint = {
    ...mapboxLayer.paint,
    'line-width': layerDefinition.style.strokeWeight || 2,
    'line-color': layerDefinition.style.strokeColor
  };
  return mapboxLayer;
}

export const createFillLayer = (layerDefinition: ILayer): mapboxgl.Layer => {
  const mapboxLayer = createMapboxLayer(layerDefinition);
  mapboxLayer.paint = {
    ...mapboxLayer.paint,
    'fill-color': layerDefinition.style.fillColor
  };
  return mapboxLayer;
}

/**
 * Convert to string, remove whitespace, make lowercase
 */
function formatForSearch(searchValue: any): string {
  return (searchValue + '').toLowerCase().replace(/\s/g, '');
}

export function searchFromFeatures<T extends Geometry>(
  searchTerm: string,
  searchOptions: LayerSearchOptions,
  featureList: MapElement<T>[],
  mappings: IRegoFeatureDefinition[] = [],
): LayerSearchResult[] {
  const formattedSearchTerm = formatForSearch(searchTerm);
  if (formattedSearchTerm.length < searchOptions.minimumSearchTermLength) {
    return [];
  }

  // Collected in a for-loop instead of with `map` to avoid extra iterations of
  // the potentially very long list with `filter` and whatnot.
  const results: LayerSearchResult[] = [];
  for (const feature of [...featureList, ...mappings]) {
    for (const prop of searchOptions.interestingProperties ?? []) {
      // searching through mappings only supports one property
      if (!(feature instanceof MapElement) && prop.key !== 'mapping') {
        continue;
      }

      const result: LayerSearchResult = {
        id: feature.id.toString(),
        propertyPriority: prop.priority || 0,
        layerPriority: searchOptions.priority || 0,
        reason: prop.reason,
        matchedValue: '', // set below
        matchType: 'equals', // set below
      };

      if (feature instanceof MapElement) {
        result.feature = feature;
        result.matchedValue = formatForSearch(feature.properties[prop.key]);
      } else {
        result.title = feature.title;
        result.center = feature.center;
        result.matchedValue = formatForSearch(feature.title);
      }

      // different match types, in priority order to improve sorting
      if (result.matchedValue === formattedSearchTerm) {
        result.matchType = 'equals';
        results.push(result);
        break;
      }
      if (
        (prop.type === 'startsWith' || prop.type === 'contains') &&
        result.matchedValue.startsWith(formattedSearchTerm)
      ) {
        result.matchType = 'startsWith';
        results.push(result);
        break;
      }
      if (prop.type === 'contains' && result.matchedValue.includes(formattedSearchTerm)) {
        result.matchType = 'contains';
        results.push(result);
        break;
      }
    }
  }
  results.sort(compareLayerSearchResults);
  // include one extra result to allow showing "more results available" text
  return results.slice(0, 11);
}

/**
 * Comparison function for `Array.prototype.sort`, for use with `LayerSearchResult` arrays.
 * @returns less than 0 if a comes first, greater than 0 if b comes first, 0 if equally important
 */
export function compareLayerSearchResults(a: LayerSearchResult, b: LayerSearchResult): number {
  // prioritize exact matches
  if (a.propertyPriority === 0 && a.matchType === 'equals' && b.matchType !== 'equals') {
    return -1;
  }
  if (b.propertyPriority === 0 && b.matchType === 'equals' && a.matchType !== 'equals') {
    return 1;
  }

  // prioritize prefix matches
  if (a.propertyPriority === 0 && a.matchType === 'startsWith' && b.matchType === 'contains') {
    return -1;
  }
  if (b.propertyPriority === 0 && b.matchType === 'startsWith' && a.matchType === 'contains') {
    return 1;
  }

  // use layer and property priorities
  const diff = (b.layerPriority + b.propertyPriority) - (a.layerPriority + a.propertyPriority);
  if (diff) {
    return diff;
  }

  // as a last resort, prioritize shortest match
  // otherwise searching "ic2" might only show "IC 20", "IC 21", etc, not "IC 2"
  return a.matchedValue.length - b.matchedValue.length;
}

/**
 * @returns true if `feature` matches `filter`
 */
function filterMatches<T extends Geometry>(filter: LayerFilter['filters'][0], feature: MapElement<T>): boolean {
  const values: string[] = Array.isArray(filter.value) ? filter.value : [filter.value];
  const prop: string | undefined = feature.properties[filter.field];
  if (filter.mode === 'inclusive') {
    return prop && values.includes(prop);
  } else {
    return !values.includes(prop);
  }
}

/**
 * @returns All elements of `featureList` that match `layerFilter`. If `layerFilter` is `undefined`, returns `featureList` in its entirety.
 */
export function applyFilter<T extends Geometry>(layerFilter: LayerFilter | undefined, featureList: MapElement<T>[]): MapElement<T>[] {
  if (!layerFilter) {
    return featureList;
  }
  if (layerFilter.mode === 'OR') {
    return featureList.filter(feature => layerFilter.filters.some(filter => filterMatches(filter, feature)));
  } else {
    return featureList.filter(feature => layerFilter.filters.every(filter => filterMatches(filter, feature)));
  }
}
